import React, { useCallback, useState } from "react";
import { Modal } from "./Modal";

export interface UploadJsonModalProps {
	show: boolean;
	onHide: () => void;
	onUpload: (parsed: any, raw: string) => void;
	title: string;
	/** Description shown below the title. */
	description?: string;
	/** Accepted file types for the input, e.g. ".json,application/json". Defaults to ".json,application/json". */
	accept?: string;
}

/**
 * Reusable modal for uploading & previewing a JSON file.
 *
 * The caller receives the parsed object and raw text via `onUpload` so it
 * can decide how to apply the data.
 */
const UploadJsonModal: React.FC<UploadJsonModalProps> = ({
	show,
	onHide,
	onUpload,
	title,
	description,
	accept = ".json,application/json"
}) => {
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<string | null>(null);
	const [parsed, setParsed] = useState<any | null>(null);
	const [error, setError] = useState<string | null>(null);

	const reset = useCallback(() => {
		setFile(null);
		setPreview(null);
		setParsed(null);
		setError(null);
	}, []);

	const handleClose = useCallback(() => {
		reset();
		onHide();
	}, [reset, onHide]);

	const handleFileSelect = useCallback((selected: File | null) => {
		setFile(selected);
		setError(null);
		setPreview(null);
		setParsed(null);

		if (!selected) return;

		const reader = new FileReader();
		reader.onload = () => {
			try {
				const text = typeof reader.result === "string" ? reader.result : "";
				setPreview(text);
				const result = JSON.parse(text);
				setParsed(result);
			} catch (err) {
				console.error(err);
				setError("Invalid JSON file. Please verify the structure and try again.");
				setParsed(null);
			}
		};
		reader.onerror = () => {
			setError("Unable to read the selected file. Please try again.");
			setParsed(null);
		};
		reader.readAsText(selected);
	}, []);

	const handleApply = useCallback(() => {
		if (!parsed) return;
		onUpload(parsed, preview ?? "");
		reset();
	}, [parsed, preview, onUpload, reset]);

	return (
		<Modal show={show} onHide={handleClose} centered size="lg">
			<Modal.Header closeButton>
				<Modal.Title>{title}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="flex flex-col gap-6 lg:flex-row">
					<div className="min-w-[320px] max-w-[360px] space-y-4">
						<div>
							<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Upload</p>
							<h2 className="text-xl font-semibold text-slate-900">Select a payload</h2>
							{description && (
								<p className="mt-2 text-sm text-slate-500">{description}</p>
							)}
						</div>
						<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">File</label>
						<input
							type="file"
							accept={accept}
							className="mt-1 block w-full cursor-pointer text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800"
							onChange={(event) => handleFileSelect(event.target.files ? event.target.files[0] : null)}
						/>
						{file && <p className="text-xs text-slate-500">Selected: {file.name}</p>}
						{error && (
							<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
						)}
						{parsed && !error && (
							<p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
								File parsed successfully. Review the preview then apply.
							</p>
						)}
						<p className="text-xs text-slate-400">Accepted format: .json (UTF-8)</p>
					</div>
					<div className="flex-1 max-h-[70vh] rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
						{preview ? (
							<pre className="h-full w-full overflow-auto rounded-xl bg-white p-4 text-xs text-slate-800">{preview}</pre>
						) : (
							<div className="flex h-full min-h-[320px] flex-col items-center justify-center text-center text-sm text-slate-500">
								<p className="text-base font-semibold text-slate-700">No preview yet</p>
								<p className="mt-2 text-xs text-slate-500">Choose a JSON file on the left to show its content here.</p>
							</div>
						)}
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={handleApply}
					disabled={!parsed}
					className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
				>
					Apply JSON
				</button>
				<button
					type="button"
					onClick={handleClose}
					className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
				>
					Close
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export default UploadJsonModal;
